import { ModelEntityType } from '@upright/common/model/input/types';
import { ImpactCategoryAndContributingProducts } from '@upright/common/model/output/scores/key-takeaways/company/types-company-key-takeaway';
import { KeyTakeaway, KeyTakeawayType } from '@upright/common/model/output/scores/key-takeaways/types';
import { Valence } from '@upright/common/model/types';
import { TopImpactData } from '../types';
import { compact } from 'lodash';
import { format } from 'd3-format';

interface WithImpactCategories {
  impactCategories: ImpactCategoryAndContributingProducts[];
}

export function getRelevantImpactCategories(
  entityType: ModelEntityType | undefined,
  valence: Valence.NEGATIVE | Valence.POSITIVE,
  takeaways: KeyTakeaway[] | undefined,
  topImpactData: TopImpactData | undefined,
) {
  // Note: This switch-case should match to the one deciding the contents of the boxes themselves. TODO: Do the content data generation and connector decision in one place.
  switch (entityType) {
    case ModelEntityType.COMPANY:
    case ModelEntityType.PRODUCT:
    case ModelEntityType.PHENOMENON:
    case ModelEntityType.MIXIN:
      if (!takeaways) {
        return undefined;
      }
      return getRelevantImpactCategoriesForTakeaways(valence, takeaways);
    case ModelEntityType.GROUP:
    case ModelEntityType.PORTFOLIO:
      if (!topImpactData) {
        return undefined;
      }
      return getRelevantImpactCategoriesForTopImpacts(valence, topImpactData);
    default:
      return undefined;
  }
}

function getRelevantImpactCategoriesForTakeaways(
  valence: Valence.NEGATIVE | Valence.POSITIVE,
  takeaways: KeyTakeaway[],
) {
  const ta = pickTakeawayForValence(valence, takeaways) as WithImpactCategories | undefined;
  if (!ta) {
    return undefined;
  }
  return ta.impactCategories.map(d => d.impactCategory);
}

function pickTakeawayForValence(valence: Valence.NEGATIVE | Valence.POSITIVE, takeaways: KeyTakeaway[]) {
  const takeaway = takeaways.find(
    d =>
      (d.type === KeyTakeawayType.BIGGEST_POSITIVE && valence === Valence.POSITIVE) ||
      (d.type === KeyTakeawayType.BIGGEST_NEGATIVE && valence === Valence.NEGATIVE),
  );
  return takeaway;
}

function getRelevantImpactCategoriesForTopImpacts(
  valence: Valence.NEGATIVE | Valence.POSITIVE,
  topImpactData: TopImpactData,
) {
  const largest = topImpactData[valence].largestImpact?.impactCode;
  const mostDistinct = topImpactData[valence].mostDistinctImpact?.impactCode;
  return compact([largest, mostDistinct]);
}

export function formatPercentileIntoTopPercent(percentile: number) {
  const roundedDown = Math.floor(percentile * 100);
  const figureToUse =
    roundedDown === 0 || roundedDown === 100 ? format('.1~r')(100 - percentile * 100) : 100 - roundedDown;
  return `${figureToUse}%`;
}

export function formatPercentileIntoTopPercentByValence(percentile: number, valence: Valence) {
  const roundedToIntegers = Math.floor(percentile * 100);
  if (valence === Valence.NEGATIVE) {
    const negFigureToUse =
      roundedToIntegers === 0 || roundedToIntegers === 100 ? format('.1~r')(percentile * 100) : roundedToIntegers;
    return `${negFigureToUse}%`;
  }
  const posFigureToUse =
    roundedToIntegers === 0 || roundedToIntegers === 100
      ? format('.1~r')(100 - percentile * 100)
      : 100 - roundedToIntegers;
  return `${posFigureToUse}%`;
}
